import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { BootcampPricingSection } from "./BootcampPricingPage";
import {
  ArrowUpRight,
  CalendarDays,
  Check,
  Laptop,
  ListChecks,
  NotebookPen,
  Video,
  CircleHelp,
  Presentation,
  UserRoundSearch,
  Wrench,
  AlarmClock,
  Brain,
} from "lucide-react";

/* ============================================================
   EDIT DI SINI
   - ADMIN_WHATSAPP_URL : link WA admin untuk tombol daftar
   - Harga bootcamp     : cari tulisan [HARGA] di bawah
   ============================================================ */
export const ADMIN_WHATSAPP_NUMBER = "6281234567890";

const ADMIN_WHATSAPP_URL =
  `https://wa.me/${ADMIN_WHATSAPP_NUMBER}?text=Halo%20admin%20Bandarmolony`;

function openAdminWhatsapp() {
  window.open(ADMIN_WHATSAPP_URL, "_blank", "noopener,noreferrer");
}

export function openPlanWhatsapp(plan: string, price: string) {
  const text = encodeURIComponent(
    `Halo admin Bandarmolony, saya mau daftar Bootcamp paket ${plan} (Rp ${price}). Minta info pembayarannya ya.`
  );
  window.open(`https://wa.me/${ADMIN_WHATSAPP_NUMBER}?text=${text}`, "_blank", "noopener,noreferrer");
}

/* ---------- helpers ---------- */

function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  window.scrollTo({ behavior: "smooth", top: el.getBoundingClientRect().top + window.scrollY - 70 });
}

export function Button({
  size = "md",
  className = "",
  onClick,
  children,
}: {
  size?: "sm" | "md" | "lg";
  className?: string;
  onClick?: () => void;
  children: ReactNode;
}) {
  const sizes = {
    sm: "px-4 py-1.5 text-xs",
    md: "px-5 py-2 text-sm",
    lg: "px-6 py-2.5 text-sm sm:text-base",
  };
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-1 rounded-full font-bold transition-colors cursor-pointer ${sizes[size]} ${className}`}
    >
      {children}
    </button>
  );
}



/* ---------- data ---------- */








/* ---------- page ---------- */

const COCOK: { t: string; g?: string | string[] }[] = [
  { t: "Trader yang ingin paham Bandarmology trading secara utuh", g: ["Bandarmology", "utuh"] },
  { t: "Trader yang ingin memahami analisa transaksi dan broker flow", g: ["analisa transaksi", "broker flow"] },
  { t: "Trader yang ingin bisa membaca bid-offer", g: "bid-offer" },
  { t: "Trader yang ingin punya framework analisis yg terstruktur", g: ["framework", "terstruktur"] },
  { t: "Trader yang ingin punya metode screening yang sistematis", g: ["screening", "sistematis"] },
  { t: "Trader yang ingin dilengkapi AI assistant untuk screening & analisa yang lebih cepat dan otomatis", g: ["AI assistant", "screening & analisa", "otomatis"] },
];

function hi(t: string, g?: string | string[]) {
  if (!g) return <>{t}</>;
  const golds = (Array.isArray(g) ? g : [g]).filter(Boolean);
  if (!golds.length) return <>{t}</>;
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(${golds.map(esc).join("|")})`, "g");
  return (
    <>
      {t.split(re).map((part, i) =>
        golds.includes(part) ? (
          <span key={i} className="text-[#C9A45C]">{part}</span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

export function BootcampLandingPage({ onJoin }: { onJoin: () => void }) {
  const navigate = useNavigate();


  return (
    <div className="min-h-screen flex flex-col bg-[#100A05] text-white">
      {/* Sticky nav */}
      <div className="sticky top-0 z-20 bg-[#100A05] text-white shadow-md">
          <div className="flex items-center justify-between px-4 py-2 max-w-6xl mx-auto w-full">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-0.5 font-extrabold tracking-tight text-sm whitespace-nowrap cursor-pointer"
            aria-label="Kembali ke halaman utama"
          >
            Bandarmolo
            <img
              src="/bootcamp-lp/logo-ny.png"
              alt="NY"
              className="h-[1em] w-auto object-contain"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                const fb = e.currentTarget.nextElementSibling as HTMLElement | null;
                if (fb) fb.style.display = "inline";
              }}
            />
            <span className="hidden text-[#C9A45C]">NY</span>
            <span className="ml-1 text-[0.625rem] font-bold tracking-[0.2em] text-white/50">BOOTCAMP</span>
          </button>
          <div className="hidden md:flex items-center gap-5 text-xs font-medium text-white/70">
            <button onClick={() => scrollToId("bm-home")} className="hover:text-white cursor-pointer">Home</button>
            <button onClick={() => scrollToId("bm-framework")} className="hover:text-white cursor-pointer">Framework</button>
            <button onClick={() => scrollToId("bm-pricing")} className="hover:text-white cursor-pointer">Jadwal</button>
            <button onClick={() => scrollToId("bm-tiers")} className="hover:text-white cursor-pointer">Pricing</button>
          </div>
          <Button
            size="sm"
            className="bg-[#C9A45C] hover:bg-[#b8944f] text-[#071A33] text-xs shrink-0"
            onClick={onJoin}
          >
            JOIN BOOTCAMP
          </Button>
          </div>
      </div>

      {/* Content */}
      <div className="flex-1">
        {/* ===== PROBLEM ===== */}
        <section id="bm-problem" className="scroll-mt-16 bg-[#F5F8FC] text-[#071A33]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8 pb-6 sm:pb-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-4">
              <span className="text-red-600">Problems Traders</span>{" "}
              <span className="text-slate-900">saat memakai analisa</span>
              <br />
              <span className="text-slate-900">Bandarmology, Smart Money Concept, dan Bid-Offer</span>
            </h2>
            <div className="rounded-3xl border-2 border-slate-200 bg-white p-2.5 sm:p-4 shadow-sm">
            <div className="space-y-2">
              {[
                {
                  level: "5",
                  badge: "bg-violet-700",
                  row: "bg-violet-50",
                  accent: "text-violet-700",
                  Icon: Brain,
                  first: <>Nggak ngerti konsep <span className="text-red-600">Money Flow</span></>,
                  second: <>&amp; cara bacanya.</>,
                },
                {
                  level: "4",
                  badge: "bg-orange-500",
                  row: "bg-orange-50",
                  accent: "text-orange-500",
                  Icon: Presentation,
                  first: <>Sudah ikut kelas bandarmology, Money Flow &amp; Bid-Offer,</>,
                  second: <span className="text-red-600">tapi belum ngerti juga.</span>,
                },
                {
                  level: "3",
                  badge: "bg-green-600",
                  row: "bg-green-50",
                  accent: "text-green-600",
                  Icon: UserRoundSearch,
                  first: <>Sudah paham konsepnya</>,
                  second: <span className="text-red-600">tapi sering bingung pas praktek.</span>,
                },
                {
                  level: "2",
                  badge: "bg-blue-600",
                  row: "bg-blue-50",
                  accent: "text-blue-600",
                  Icon: Wrench,
                  first: <>Paham konsepnya, sudah bisa prakteknya,</>,
                  second: <span className="text-red-600">tapi gak dapat tools yang sesuai dengan isi kepala kita.</span>,
                },
                {
                  level: "1",
                  badge: "bg-red-600",
                  row: "bg-rose-50",
                  accent: "text-red-600",
                  Icon: AlarmClock,
                  first: <>Paham konsep, Bisa Praktek, sudah punya tools,</>,
                  second: <span className="text-red-600">tapi screening &amp; analisanya ngabisin waktu banget.</span>,
                },
              ].map((r) => (
                <div key={r.level} className={`flex items-center gap-3 rounded-2xl ${r.row} p-2.5 sm:p-3`}>
                  <div className={`flex h-14 w-14 sm:h-16 sm:w-16 shrink-0 flex-col items-center justify-center rounded-xl ${r.badge} text-white`}>
                    <span className="text-[10px] font-bold tracking-widest">LEVEL</span>
                    <span className="text-2xl sm:text-3xl font-extrabold leading-none">{r.level}</span>
                  </div>
                  <div className={`hidden sm:block w-px self-stretch ${r.badge} opacity-30`} />
                  <div className="flex-1 min-w-0 text-sm sm:text-lg font-bold leading-snug text-slate-900">
                    <p>{r.first}</p>
                    <p>{r.second}</p>
                  </div>
                  <span className="flex h-12 w-12 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-full bg-white shadow-sm">
                    <r.Icon className={`h-6 w-6 sm:h-7 sm:w-7 ${r.accent}`} strokeWidth={2} />
                  </span>
                </div>
              ))}
            </div>
            </div>
          </div>
        </section>

        {/* ===== HERO ===== */}
        <section id="bm-home" className="relative overflow-hidden bg-[#100A05] text-white flex flex-col">
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div
              className="absolute inset-0"
              style={{ background: "radial-gradient(700px 320px at 80% 10%, rgba(22,140,240,0.22), transparent 70%)" }}
            />
            <div
              className="absolute left-[-6.25rem] top-[-5rem] h-[30rem] w-[30rem] rounded-full"
              style={{ background: "radial-gradient(circle, rgba(217,119,6,0.55) 0%, rgba(217,119,6,0.22) 42%, transparent 68%)" }}
            />
            <div
              className="absolute left-[-8.75rem] top-[45%] h-[26.25rem] w-[26.25rem] rounded-full"
              style={{ background: "radial-gradient(circle, rgba(180,83,9,0.5) 0%, rgba(180,83,9,0.2) 42%, transparent 68%)" }}
            />
            <div
              className="absolute right-[-5rem] top-[-3.75rem] h-[30rem] w-[30rem] rounded-full"
              style={{ background: "radial-gradient(circle, rgba(217,119,6,0.55) 0%, rgba(217,119,6,0.22) 42%, transparent 68%)" }}
            />
            <div
              className="absolute right-[-10rem] top-[35%] h-[25rem] w-[25rem] rounded-full"
              style={{ background: "radial-gradient(circle, rgba(217,119,6,0.45) 0%, rgba(217,119,6,0.18) 42%, transparent 68%)" }}
            />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-4 lg:pt-10 lg:pb-4 grid lg:grid-cols-[1.05fr_0.95fr] gap-6 items-center w-full">
            <div className="lg:self-stretch flex flex-col">
              <p className="text-[0.6875rem] font-bold tracking-[0.3em] text-[#C9A45C] mb-5">BANDARMOLONY TRADING BOOTCAMP</p>
              <h1 className="text-4xl sm:text-5xl font-extrabold leading-[1.08] tracking-tight xl:whitespace-nowrap">
                Upgrade Skill Trading Mu!
                <span className="block mt-2 text-2xl sm:text-[1.6875rem] font-extrabold text-white/90">
                  Dengan kemampuan membaca <span className="text-[#C9A45C]">gerakan Bandar</span>
                </span>
              </h1>
              <div className="mt-10 space-y-2 text-[0.9375rem] leading-relaxed text-white/75">
                <p>Bukan sekedar membaca chart &amp; data, pelajari cara menghubungkan:</p>
                <p className="font-bold text-white">
Transaction Analysis <span className="text-[#C9A45C] mx-1">•</span> Technical <span className="text-[#C9A45C] mx-1">•</span> Bid-Offer
                </p>
                <p>Untuk meningkatkan winning rate mu!</p>
              </div>
              <div className="mt-6 lg:mt-auto lg:pt-6 flex flex-col sm:flex-row sm:items-center gap-3">
                <Button
                  size="lg"
                  className="bg-[#C9A45C] hover:bg-[#b8944f] text-[#071A33] shadow-lg shadow-[#C9A45C]/25"
                  onClick={onJoin}
                >
                  JOIN BANDARMOLONY TRADING BOOTCAMP <ArrowUpRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>

            {/* Laptop mockup - ala landing page utama */}
            <div className="relative w-full flex justify-center items-start lg:self-stretch lg:min-h-0 lg:items-center">
              <div className="relative w-full flex flex-col items-center justify-start lg:min-h-0">
                {/* red glow */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-[#C9A45C]/25 blur-[100px] rounded-full pointer-events-none z-0" />
                {/* screen frame */}
                <div className="relative z-10 w-full max-w-full aspect-[847/570] lg:max-h-full rounded-t-xl rounded-b-sm border-[4px] md:border-[6px] border-black bg-gray-950 p-1 shadow-[0_0_60px_rgba(201,164,92,0.35)]">
                  <div className="absolute top-1 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-gray-600 ring-1 ring-black z-20" />
                  <div className="relative w-full rounded-sm overflow-hidden bg-black">
                    <img
                      src="/bootcamp-lp/laptop.png?v=4"
                      alt="Contoh analisis Bandarmolony: broker summary, running trade, dan chart DSSA"
                      className="w-full h-auto object-cover"
                      loading="lazy"
                    />
                  </div>
                  {/* laptop base */}
                  <div className="absolute top-[100%] left-1/2 -translate-x-1/2 w-[108%] h-4 bg-gradient-to-b from-gray-400 via-gray-600 to-gray-900 rounded-b-xl shadow-2xl flex justify-center items-start border-t-2 border-gray-300 z-30">
                    <div className="w-1/6 h-1.5 bg-gray-900 rounded-b-md opacity-70" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pb-14 mt-4 w-full grid sm:grid-cols-3 gap-3 shrink-0">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#C9A45C]/15 shrink-0">
                <Laptop className="h-7 w-7 text-[#C9A45C]" />
              </div>
              <div>
                <h3 className="font-bold text-sm mb-1">Metode Belajar Terstruktur</h3>
                <p className="text-xs text-white/60 leading-relaxed">Teori + Sample + Exercise + Diskusi + QNA &amp; REPEAT!</p>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#C9A45C]/15 shrink-0">
                <ListChecks className="h-7 w-7 text-[#C9A45C]" />
              </div>
              <div>
                <h3 className="font-bold text-sm mb-1">Analisis Sistematis</h3>
                <p className="text-xs text-white/60 leading-relaxed">Pahami hubungan data transaksi, bid-offer dan pergerakan harga.</p>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#C9A45C]/15 shrink-0">
                <Video className="h-7 w-7 text-[#C9A45C]" />
              </div>
              <div>
                <h3 className="font-bold text-sm mb-1">Kelas Online Intensive</h3>
                <p className="text-xs text-white/60 leading-relaxed">20x sesi pertemuan via Zoom dengan studi kasus real market.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ===== PROBLEM (hidden for now) ===== */}
        {/*
        <section id="bm-program" className="scroll-mt-16 bg-[#F5F8FC] text-[#071A33]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="text-center max-w-2xl mx-auto">
            <SectionEyebrow>MASALAHNYA</SectionEyebrow>
            <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight mb-2">
              Masalahnya Bukan Kurang Banyak Indikator.
            </h2>
            <p className="text-lg sm:text-xl font-bold mt-3">
              Dengan kemampuan membaca gerakan Bandar
            </p>
            <div className="mt-5 space-y-2.5 text-sm sm:text-[0.9375rem] leading-relaxed text-slate-500">
              <p>Bukan sekedar membaca chart, pelajari cara menghubungkan:</p>
              <p className="font-bold text-[#071A33]">
                Transaction Analysis <span className="text-[#C9A45C] mx-1">–</span> Technical <span className="text-[#C9A45C] mx-1">–</span> Bid-Offer
              </p>
              <p>Untuk analisis yang lebih sistematis.</p>
            </div>
            <button onClick={() => scrollToId("bm-framework")} className="mt-6 text-xs font-bold tracking-widest text-[#168CF0] hover:text-[#071A33] cursor-pointer">
              LIHAT FRAMEWORK ↓
            </button>
          </div>
          </div>
        </section>
        */}

        {/* ===== FRAMEWORK ===== */}
        <section id="bm-framework" className="scroll-mt-16 bg-[#F5F8FC] text-[#071A33]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="max-w-2xl mb-8">
            <h2 className="text-2xl sm:text-[2.75rem] sm:leading-[1.1] font-extrabold tracking-tight sm:whitespace-nowrap">
              BANDARMOLONY FRAMEWORK ANALYSIS
            </h2>
            <p className="mt-2 text-sm sm:text-base text-slate-500">Satu framework untuk membaca market secara utuh</p>
          </div>
          <div className="w-full rounded-2xl overflow-hidden border border-slate-200 shadow-lg bg-white">
            <img
              src="/bootcamp-lp/framework-light-ok4.avif"
              alt="Bagan Bandarmolony Analysis Framework terbaru"
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </div>
          </div>
        </section>

        {/* ===== FIT CHECK ===== */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="relative rounded-3xl border border-[#C9A45C]/60 bg-[#0A0F1E] shadow-[0_0_80px_rgba(201,164,92,0.2)] overflow-hidden">
            <img
              src="/bootcamp-lp/cocok-bg.jpeg?v=3"
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F1E] via-[#0A0F1E]/75 to-[#0A0F1E]/20" />
            <div className="relative grid md:grid-cols-[1.2fr_0.8fr] gap-6 p-6 sm:p-10">
              <div>
                <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-6">
                  COCOK <span className="text-[#C9A45C]">UNTUK</span>
                </h2>
                <ul className="space-y-4">
                  {COCOK.map((it) => (
                    <li key={it.t} className="flex items-start gap-3 text-[0.9375rem] text-white/85">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500 text-white shrink-0">
                        <Check className="h-4 w-4" />
                      </span>
                      <span className="pt-1">{hi(it.t, it.g)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div />
            </div>
          </div>
        </section>

        {/* ===== WHAT YOU GET + OFFER ===== */}
        <section id="bm-pricing" className="scroll-mt-16 bg-[#F5F8FC] text-[#071A33]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 grid lg:grid-cols-2 gap-8 items-stretch">
            <div className="flex flex-col gap-4 h-full">
              <div className="rounded-2xl bg-[#3A240A] border border-white/10 p-5 sm:p-6 flex items-center gap-4 flex-1 text-white">
                <span className="flex h-24 w-24 items-center justify-center shrink-0">
                  <img
                    src="/bootcamp-lp/pointing.png?v=3"
                    alt="Batch bootcamp"
                    className="h-24 w-auto object-contain"
                    loading="lazy"
                  />
                </span>
                <div className="border-l border-white/15 pl-4">
                  <p className="text-3xl font-extrabold">Batch 7 Nov 2026</p>
                  <p className="text-lg text-white/70 mt-1">Live via Zoom • 20x sesi</p>
                </div>
              </div>
              <div className="rounded-2xl bg-[#3A240A] border border-white/10 p-5 sm:p-6 flex-1 flex items-center gap-4 text-white">
                <span className="flex h-24 w-24 items-center justify-center rounded-xl bg-[#100A05]/30 shrink-0 self-center">
                  <CalendarDays className="h-12 w-12 text-[#C9A45C]" />
                </span>
                <div className="border-l border-white/15 pl-4">
                  <p className="text-3xl font-extrabold text-[#C9A45C]">Jadwal Zoom</p>
                  <div className="space-y-2 text-lg mt-2">
                    <p className="flex gap-3"><span className="w-36 shrink-0 font-semibold whitespace-nowrap">Sabtu</span><span>: Jam 10.00 AM</span></p>
                    <p className="flex gap-3"><span className="w-36 shrink-0 font-semibold whitespace-nowrap">Senin - Kamis</span><span>: Jam 08.00 PM</span></p>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl bg-[#3A240A] border border-white/10 p-5 sm:p-6 flex items-center gap-4 flex-1 text-white">
                <span className="flex h-24 w-24 items-center justify-center rounded-xl bg-[#100A05]/30 shrink-0">
                  <NotebookPen className="h-12 w-12 text-[#C9A45C]" />
                </span>
                <div className="border-l border-white/15 pl-4 space-y-1 text-2xl">
                  <p><span className="font-semibold">1.</span> Semua sesi Zoom direkam</p>
                  <p><span className="font-semibold">2.</span> Semua handout materi</p>
                </div>
              </div>
            </div>
            <div>
              <div className="rounded-3xl bg-white text-[#071A33] p-5 sm:p-8 shadow-2xl lg:sticky lg:top-20 border border-[#C9A45C]">
              <p className="text-[0.6875rem] font-extrabold tracking-[0.25em] text-[#168CF0] mb-1">JOIN BANDARMOLONY BOOTCAMP</p>
              <p className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-1 text-[#5C3317]">
                Rp 9.999.000
              </p>
              <p className="text-xs text-slate-400 mb-4">Satu kali enrollment.</p>
              <p className="text-xs font-bold mb-2">Benefits :</p>
              <ul className="space-y-1.5 mb-5 text-xs">
                <li>
                  <p className="flex items-start gap-2 text-[#071A33] font-medium">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    Full Bootcamp Framework material
                  </p>
                  <ul className="mt-1.5 ml-6 space-y-1.5">
                    {["Bandar Screening 360 degree", "Bandar Classification", "Bid-Offer Analysis", "Technical Analysis"].map((sub) => (
                      <li key={sub} className="flex items-center gap-2 text-blue-700">
                        <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
                        {sub}
                      </li>
                    ))}
                  </ul>
                </li>
                {["Full Zoom Video Recording", "Free Access to Bandarmolony AI Assistant", "Free Coaching & Mentoring in Trading Group for 1 year", "Early Bird Discount on early registration"].map((it) => (
                  <li key={it} className="flex items-start gap-2 text-[#071A33] font-medium">
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    {it}
                  </li>
                ))}
              </ul>
              <Button
                size="lg"
                className="w-full bg-[#4A2A0C] hover:bg-[#3A2008] text-white"
                onClick={onJoin}
              >
                DAFTAR BANDARMOLONY BOOTCAMP <ArrowUpRight className="h-4 w-4 ml-1" />
              </Button>
                <p className="text-center text-xs font-extrabold tracking-wide text-[#EA580C] mt-2">Tersedia Super Early Bird sampai 5 October</p>
                <p className="text-center text-[0.6875rem] text-slate-400 mt-1">Secure your access today.</p>
          </div>
          </div>
          </div>
        </section>

        {/* ===== PRICING TIERS ===== */}
        <section id="bm-tiers" className="scroll-mt-16 bg-[#17100A] text-white relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div
              className="absolute left-1/2 top-[-120px] h-[380px] w-[680px] -translate-x-1/2 rounded-full"
              style={{ background: "radial-gradient(ellipse, rgba(217,119,6,0.22) 0%, transparent 65%)" }}
            />
            <div
              className="absolute right-[-100px] bottom-[-80px] h-[360px] w-[360px] rounded-full"
              style={{ background: "radial-gradient(circle, rgba(201,164,92,0.18) 0%, transparent 65%)" }}
            />
          </div>
          <BootcampPricingSection />
        </section>

        {/* ===== TRUST (hidden for now) ===== */}
        {/*
        <section className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 sm:p-6 flex gap-3 shadow-sm">
            <ShieldCheck className="h-6 w-6 text-[#168CF0] shrink-0" />
            <div>
              <h3 className="font-extrabold text-sm mb-1">Belajar Dengan Ekspektasi Yang Realistis.</h3>
              <p className="text-xs text-white/60 leading-relaxed">
                Bandarmolony Bootcamp adalah program edukasi. Materi membantu peserta memahami
                framework analisis market, bukan memberikan jaminan keuntungan atau rekomendasi
                transaksi tertentu.
              </p>
            </div>
          </div>
        </section>
        */}

        {/* ===== FOOTER ===== */}
        <footer className="bg-[#100A05] text-white/50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="flex items-center gap-0.5 font-extrabold tracking-tight text-sm whitespace-nowrap">
              <span className="text-white">Bandarmolo</span>
              <img
                src="/bootcamp-lp/logo-ny.png?v=3"
                alt="NY"
                className="h-[1em] w-auto object-contain"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  const fb = e.currentTarget.nextElementSibling as HTMLElement | null;
                  if (fb) fb.style.display = "inline";
                }}
              />
              <span className="hidden text-[#C9A45C]">NY</span>
              <span className="ml-1 text-[0.625rem] font-bold tracking-[0.2em] text-white/50">BOOTCAMP</span>
            </span>
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs">
              {/* <button onClick={() => scrollToId("bm-program")} className="hover:text-white cursor-pointer">Program</button> */}

              <button onClick={openAdminWhatsapp} className="hover:text-white cursor-pointer">Contact</button>
            </div>
            <p className="text-[0.6875rem]">© 2026 Bandarmolony. All rights reserved.</p>
          </div>
        </footer>

        {/* Mobile sticky CTA */}
        <div className="sticky bottom-0 z-20 sm:hidden bg-[#100A05]/95 backdrop-blur border-t border-white/10 px-4 py-2.5">
          <Button
            className="w-full bg-[#C9A45C] hover:bg-[#b8944f] text-[#071A33]"
            onClick={onJoin}
          >
            JOIN BANDARMOLONY BOOTCAMP <ArrowUpRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
